import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, FolderOpen, FolderPlus, Search, X } from 'lucide-react';
import { useActiveProject } from '../context/ActiveProjectContext';
import { useAuth } from '../context/AuthContext';
import NewProjectFormScreen from './NewProjectFormScreen';

const ProjectPickerModal = () => {
  const { canManageProjects } = useAuth();
  const {
    pickerOpen,
    closeProjectPicker,
    projects,
    selectedProject,
    selectProject,
    createAndSelectProject,
    refreshProjects,
    loading,
  } = useActiveProject();

  const [search, setSearch] = useState('');
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const query = search.trim().toLowerCase();
  const canRunSearch = query.length >= 1;

  const filteredProjects = useMemo(() => {
    if (!canRunSearch) return projects;
    return projects.filter((p) => String(p?.name || '').toLowerCase().includes(query));
  }, [projects, canRunSearch, query]);

  const matchedProject = useMemo(() => {
    if (!canRunSearch) return null;
    return projects.find((p) => String(p?.name || '').toLowerCase().includes(query)) || null;
  }, [projects, canRunSearch, query]);

  useEffect(() => {
    if (!pickerOpen) return;
    if (loading) return;
    if (projects.length > 0) return;

    setRefreshing(true);
    refreshProjects()
      .catch(() => {
        // Keep UI usable; existing error surfaces on user action.
      })
      .finally(() => setRefreshing(false));
  }, [pickerOpen, loading, projects.length, refreshProjects]);

  if (!pickerOpen) return null;

  const handleSelect = async (project) => {
    setError('');
    try {
      await selectProject(project);
      closeProjectPicker();
      setSearch('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to select project');
    }
  };

  const handleCreateFromForm = async (payload) => {
    await createAndSelectProject(payload);
    setShowNewProjectForm(false);
    closeProjectPicker();
    setSearch('');
  };

  const openProjectForm = () => {
    setError('');
    setShowNewProjectForm(true);
  };

  return (
    <>
      <div className="fixed inset-0 z-[80] flex items-center justify-center px-3 py-4 sm:px-6 sm:py-6 backdrop-blur-sm" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div
        className="w-full max-w-lg max-h-[88vh] overflow-hidden rounded-2xl bg-white shadow-2xl border"
        style={{ borderColor: '#f4c2cf' }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: '#f4c2cf', background: 'linear-gradient(180deg,#fff8fa 0%,#fff2f6 100%)' }}
        >
          <div>
            <h3 className="text-base font-bold text-slate-900">Select Project</h3>
            <p className="text-xs text-slate-400">Choose a project to set as active</p>
          </div>

          <button
            type="button"
            onClick={closeProjectPicker}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-pink-100 transition"
          >
            <X size={18} color="#c44569" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(88vh-60px)]">
          <div className="relative flex items-center">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project by name..."
              className="w-full pl-10 pr-4 py-3 rounded-2xl border text-sm outline-none transition"
              style={{ borderColor: '#f4c2cf', background: '#fff7fa' }}
            />
          </div>

          {error ? (
            <div className="mt-3 rounded-xl px-4 py-3 text-sm" style={{ background: '#fce0e8', color: '#c44569' }}>
              {error}
            </div>
          ) : null}

          {(loading || refreshing) ? (
            <div className="mt-4 rounded-xl p-3 text-sm text-slate-500 border border-slate-200 bg-slate-50">
              Loading projects...
            </div>
          ) : (
            <>
              {canRunSearch && matchedProject ? (
                <div
                  className="mt-4 p-3 rounded-2xl border flex items-center justify-between gap-3"
                  style={{ borderColor: '#16a34a', background: '#f0fdf4' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle2 size={20} color="#16a34a" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-700 truncate">{matchedProject.name}</p>
                      <p className="text-xs text-slate-500 truncate">{matchedProject.description || 'No description'}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleSelect(matchedProject)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition"
                    style={{ background: '#16a34a' }}
                  >
                    Select
                  </button>
                </div>
              ) : null}

              {canRunSearch && !matchedProject ? (
                <div
                  className="mt-4 p-4 rounded-2xl border flex flex-wrap items-center justify-between gap-3"
                  style={{ borderColor: '#f9b8ca', background: '#fff6f8' }}
                >
                  <div className="flex items-center gap-2">
                    <FolderPlus size={16} color="#c44569" />
                    <p className="text-sm font-semibold text-slate-700">
                      No project matched "{search.trim()}"
                    </p>
                  </div>

                  {canManageProjects ? (
                    <button
                      onClick={openProjectForm}
                      className="text-xs font-semibold px-4 py-2 rounded-xl text-white"
                      style={{ background: '#c44569' }}
                    >
                      Add New Project
                    </button>
                  ) : (
                    <p className="text-xs font-medium text-slate-500">
                      You can select existing projects only.
                    </p>
                  )}
                </div>
              ) : null}

              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FolderOpen size={18} color="#c44569" />
                    <h4 className="text-sm font-bold text-slate-800">All Existing Projects</h4>
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-500">
                    {filteredProjects.length}
                  </span>
                </div>

                {filteredProjects.length === 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    No projects found. Use Add New Project to create one.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredProjects.map((project) => {
                      const active = String(selectedProject?.id || '') === String(project.id);
                      return (
                        <button
                          type="button"
                          key={project.id}
                          onClick={() => handleSelect(project)}
                          className="text-left rounded-2xl border p-4 transition-all flex items-start gap-3"
                          style={
                            active
                              ? {
                                  borderColor: '#16a34a',
                                  background: '#f0fdf4',
                                  boxShadow: '0 6px 16px rgba(22,163,74,0.12)',
                                }
                              : { borderColor: '#fce0e8', background: '#fff' }
                          }
                        >
                          {active ? (
                            <CheckCircle2 size={18} color="#16a34a" className="shrink-0 mt-0.5" />
                          ) : (
                            <FolderOpen size={18} color="#c44569" className="shrink-0 mt-0.5" />
                          )}

                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-slate-800 truncate">{project.name}</p>
                            <p className="text-xs text-slate-500 mt-1 truncate">
                              {project.description || 'No description'}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      <NewProjectFormScreen
        isOpen={showNewProjectForm && canManageProjects}
        initialName={search.trim()}
        onClose={() => setShowNewProjectForm(false)}
        onSubmit={handleCreateFromForm}
      />
    </>
  );
};

export default ProjectPickerModal;
